import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { overlayClose } from "../../../../shared/utils/overlayClose";

// ── Estilos compartidos del modal ────────────────────────────────────────────
const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
};
const MODAL = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "var(--bg-card)",
  border: "1px solid var(--border-card)",
  borderRadius: 12,
  padding: "24px",
  width: "min(480px,93vw)",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};
const LABEL_STYLE = {
  display: "block",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: 5,
};
const INPUT_STYLE = {
  width: "100%",
  background: "var(--bg-input)",
  border: "1px solid var(--border-card)",
  borderRadius: 8,
  padding: "9px 12px",
  color: "var(--text-primary)",
  fontSize: "0.9rem",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

function ModalHeader({ icon, title, subtitle, onClose, iconColor = "#f97316" }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            background: `${iconColor}22`,
            border: `1px solid ${iconColor}44`,
            borderRadius: 9,
            padding: "9px 11px",
            color: iconColor,
          }}
        >
          <i className={`fa-solid ${icon}`} style={{ fontSize: "1.1rem" }} />
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              color: "var(--text-primary)",
              fontSize: "1rem",
              fontWeight: 700,
            }}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              style={{
                margin: "2px 0 0",
                color: "var(--text-muted)",
                fontSize: "0.77rem",
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          fontSize: "1.1rem",
        }}
        onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
        onMouseOut={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
      >
        ✕
      </button>
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}

function AlertBox({ type, children }) {
  const map = {
    warning: {
      bg: "rgba(251,191,36,0.08)",
      border: "rgba(251,191,36,0.3)",
      left: "#fbbf24",
      color: "#fde68a",
    },
    danger: {
      bg: "rgba(239,68,68,0.08)",
      border: "rgba(239,68,68,0.3)",
      left: "#ef4444",
      color: "#fca5a5",
    },
    info: {
      bg: "rgba(96,165,250,0.08)",
      border: "rgba(96,165,250,0.3)",
      left: "#60a5fa",
      color: "#bfdbfe",
    },
    success: {
      bg: "rgba(74,222,128,0.08)",
      border: "rgba(74,222,128,0.3)",
      left: "#4ade80",
      color: "#bbf7d0",
    },
  };
  const s = map[type] ?? map.info;
  return (
    <div
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderLeft: `3px solid ${s.left}`,
        borderRadius: 6,
        padding: "10px 14px",
        color: s.color,
        fontSize: "0.83rem",
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DELETE MODAL
// ═══════════════════════════════════════════════════════════════════════════

export function DeleteReceiptModal({ receipt, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [detalles, setDetalles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/api/finance/recibo/${receipt.id}/detalles-info/`)
      .then((r) => r.json())
      .then(setDetalles)
      .catch(() => setDetalles({ tiene_detalles: false, detalles: [] }))
      .finally(() => setLoading(false));
  }, [receipt.id]);

  async function handleDelete() {
    setDeleting(true);
    setError("");
    try {
      const url = receipt.es_deposito_sin_identificar
        ? `/api/finance/recibo/${receipt.id}/revertir-deposito/`
        : `/api/finance/recibo/${receipt.id}/`;
      const method = receipt.es_deposito_sin_identificar ? "POST" : "DELETE";
      const res = await apiFetch(url, { method });
      if (!res.ok && res.status !== 204)
        throw new Error(t("recibos.reciboEliminado"));
      onSuccess(
        receipt.es_deposito_sin_identificar
          ? t("recibos.depositoRevertido")
          : t("recibos.reciboEliminado"),
      );
      onClose();
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  }

  const tieneFacturas = detalles?.tiene_detalles;

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={MODAL}>
        <ModalHeader
          icon="fa-trash"
          title={`${t("recibos.eliminarReciboTitulo")} ${receipt.numero_recibo}`}
          subtitle={`${receipt.moneda_pago === 'USD' ? '$' : '₡'}${formatCurrency(receipt.monto)} · ${receipt.filial?.idFilial}`}
          onClose={onClose}
          iconColor="#f87171"
        />
        <Divider />

        {loading ? (
          <p style={{ color: "var(--text-secondary)", textAlign: "center" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            {t("recibos.verificandoFacturasAplic")}
          </p>
        ) : (
          <>
            {tieneFacturas ? (
              <>
                <AlertBox type="danger">
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                    {t("recibos.reciboTieneNFacturas", { n: detalles.detalles.length })}
                  </div>
                  {t("recibos.eliminandoRevertira")}
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {detalles.detalles.map((d, i) => (
                      <li key={i}>
                        <strong>{d.factura}</strong> — {t("recibos.abonoDe")} ₡
                        {formatCurrency(d.abono)}
                      </li>
                    ))}
                  </ul>
                </AlertBox>
                <AlertBox type="warning">
                  <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
                  {t("recibos.sistemaRevertiraAbonos")}
                </AlertBox>
              </>
            ) : (
              <AlertBox type="info">
                <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
                {t("recibos.sinFacturasEliminar")}
              </AlertBox>
            )}

            {error && (
              <AlertBox type="danger">
                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }} />
                {error}
              </AlertBox>
            )}
          </>
        )}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-muted)",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            {t("common.cancelar")}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || loading}
            style={{
              padding: "8px 20px",
              background: deleting ? "rgba(239,68,68,0.5)" : "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: deleting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {deleting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("common.eliminando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-trash" />{" "}
                {tieneFacturas ? t("recibos.eliminarRevertir") : t("recibos.eliminarReciboBtn")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════

export function EditReceiptModal({ receipt, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [detalles, setDetalles] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({
    monto: String(receipt.monto),
    fecha_pago: receipt.fecha_pago,
    metodo_pago: receipt.metodo_pago,
    documento: receipt.documento ?? "",
    descripcion: receipt.descripcion ?? "",
    cuenta_bancaria:
      receipt.cuenta_bancaria?.id ?? receipt.cuenta_bancaria ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/api/finance/recibo/${receipt.id}/detalles-info/`)
      .then((r) => r.json())
      .then(setDetalles)
      .catch(() =>
        setDetalles({
          tiene_detalles: false,
          detalles: [],
          total_aplicado: "0",
        }),
      );

    apiFetch("/api/finance/cuentaBancaria/")
      .then((r) => r.json())
      .then((d) => setCuentas(d.results ?? d))
      .catch(() => {});
  }, [receipt.id]);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // ── Análisis del cambio de monto ─────────────────────────────────────
  const montoNuevo    = Number(form.monto) || 0;
  const montoOriginal = Number(receipt.monto);
  const aplicado      = Number(detalles?.total_aplicado ?? 0);
  const tieneFacturas = detalles?.tiene_detalles;
  const numFacturas   = detalles?.detalles?.length ?? 0;

  const esSoloSaldoFavor   = montoOriginal === 0;
  const esMultiplesFacturas = numFacturas > 1;

  const diferencia    = montoNuevo - montoOriginal;
  const quedaDebiendo = tieneFacturas && montoNuevo < aplicado;
  const hayExcedente  = tieneFacturas && montoNuevo > aplicado;
  const excedente     = tieneFacturas ? montoNuevo - aplicado : 0;

  async function handleSave() {
    if (!form.fecha_pago) {
      setError(t("recibos.fechaObligatoria"));
      return;
    }
    if (!esSoloSaldoFavor && !esMultiplesFacturas && montoNuevo <= 0) {
      setError(t("recibos.montoMayorCero"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/recibo/${receipt.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          ...(!(esSoloSaldoFavor || esMultiplesFacturas) && { monto: montoNuevo }),
          fecha_pago: form.fecha_pago,
          metodo_pago: form.metodo_pago,
          documento: form.documento,
          descripcion: form.descripcion,
          cuenta_bancaria: Number(form.cuenta_bancaria) || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? t("recibos.errorActualizar"));
      }
      onSuccess(t("recibos.reciboActualizado"));
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const iStyle = { ...INPUT_STYLE };

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={{ ...MODAL, width: "min(520px,93vw)" }}>
        <ModalHeader
          icon="fa-pencil"
          title={`${t("recibos.editarReciboTitulo")} ${receipt.numero_recibo}`}
          subtitle={`${t("common.filial")} ${receipt.filial?.idFilial} · ${t("recibos.originalMonto")} ${receipt.moneda_pago === 'USD' ? '$' : '₡'}${formatCurrency(montoOriginal)}`}
          onClose={onClose}
        />
        <Divider />

        {/* CASO 2: Solo saldo a favor */}
        {esSoloSaldoFavor && (
          <AlertBox type="danger">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              <i className="fa-solid fa-ban" style={{ marginRight: 6 }} />
              {t("recibos.soloSaldoFavorTitulo")}
            </div>
            {t("recibos.soloSaldoFavorDesc")}
          </AlertBox>
        )}

        {/* CASO 4: Múltiples facturas */}
        {!esSoloSaldoFavor && esMultiplesFacturas && (
          <AlertBox type="danger">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              <i className="fa-solid fa-ban" style={{ marginRight: 6 }} />
              {t("recibos.multiplesFacturasTitulo", { n: numFacturas })}
            </div>
            {t("recibos.multiplesFacturasDesc")}
          </AlertBox>
        )}

        {/* ── Advertencias por cambio de monto ─────────────────────── */}
        {tieneFacturas &&
          diferencia !== 0 &&
          (quedaDebiendo ? (
            <AlertBox type="danger">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                {t("recibos.montoMenorAplicadoTitulo", { aplicado: formatCurrency(aplicado) })}
              </div>
              {t("recibos.montoMenorAplicadoDesc", {
                nuevo: formatCurrency(montoNuevo),
                diferencia: formatCurrency(aplicado - montoNuevo),
              })}
            </AlertBox>
          ) : hayExcedente ? (
            <AlertBox type="warning">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
                {t("recibos.excedenteTitulo", { excedente: formatCurrency(excedente) })}
              </div>
              {t("recibos.excedenteDesc")}
            </AlertBox>
          ) : null)}

        {tieneFacturas && diferencia === 0 && (
          <AlertBox type="info">
            <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
            {t("recibos.montoNoModifico", { aplicado: formatCurrency(aplicado) })}
          </AlertBox>
        )}

        {!tieneFacturas && (
          <AlertBox type="success">
            <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
            {t("recibos.sinFacturasLibre")}
          </AlertBox>
        )}

        {/* ── Formulario ─────────────────────────────────────────── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            marginTop: 8,
          }}
        >
          <div>
            <label style={LABEL_STYLE}>
              {t("common.monto")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              style={{
                ...iStyle,
                borderColor: quedaDebiendo ? "#ef4444" : "var(--border-card)",
                opacity: esSoloSaldoFavor || esMultiplesFacturas ? 0.5 : 1,
                cursor: esSoloSaldoFavor || esMultiplesFacturas ? "not-allowed" : undefined,
              }}
              value={form.monto}
              onChange={(e) => setField("monto", e.target.value)}
              disabled={esSoloSaldoFavor || esMultiplesFacturas}
              title={
                esSoloSaldoFavor || esMultiplesFacturas
                  ? t("recibos.montoNoEditable")
                  : undefined
              }
              onFocus={(e) =>
                (e.target.style.borderColor = quedaDebiendo ? "#ef4444" : "#f97316")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = quedaDebiendo ? "#ef4444" : "var(--border-card)")
              }
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>
              {t("invoices.fechaDePago")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="date"
              style={iStyle}
              value={form.fecha_pago}
              onChange={(e) => setField("fecha_pago", e.target.value)}
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "var(--border-card)")}
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>{t("invoices.medioPago")}</label>
            <select
              style={{ ...iStyle, cursor: "pointer" }}
              value={form.metodo_pago}
              onChange={(e) => setField("metodo_pago", e.target.value)}
            >
              <option value="TRANSFERENCIA">{t("invoices.transferencia")}</option>
              <option value="EFECTIVO">{t("invoices.efectivo")}</option>
              <option value="CHEQUE">{t("invoices.cheque")}</option>
              <option value="TARJETA">{t("invoices.tarjeta")}</option>
              {/* Un recibo de ajuste (saldo inicial) no se crea desde este
                  formulario, pero si se edita no puede cambiar de método en
                  silencio: sin su opción el select caía en la primera. */}
              {form.metodo_pago === "AJUSTE" && <option value="AJUSTE">{t("recibos.ajuste")}</option>}
            </select>
          </div>

          <div>
            <label style={LABEL_STYLE}>{t("invoices.cuentaBancaria")}</label>
            <select
              style={{ ...iStyle, cursor: "pointer" }}
              value={form.cuenta_bancaria}
              onChange={(e) => setField("cuenta_bancaria", e.target.value)}
            >
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.banco} — {c.numero_cuenta}
                </option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t("recibos.referenciaDocs")}</label>
            <input
              type="text"
              style={iStyle}
              value={form.documento}
              onChange={(e) => setField("documento", e.target.value)}
              placeholder={t("invoices.referenciaPlaceholder")}
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "var(--border-card)")}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>
              {t("recibos.descripcionAnexo")}{" "}
              <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.75rem" }}>
                {t("recibos.descripcionOpcionalAnexo")}
              </span>
            </label>
            <input
              type="text"
              style={iStyle}
              value={form.descripcion}
              onChange={(e) => setField("descripcion", e.target.value)}
              placeholder={t("recibos.descripcionAnexoPlaceholder")}
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "var(--border-card)")}
            />
          </div>
        </div>

        {/* ── Detalles de facturas aplicadas (info) ──────────────── */}
        {tieneFacturas && detalles?.detalles?.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary
              style={{
                cursor: "pointer",
                color: "var(--text-secondary)",
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              <i className="fa-solid fa-list" style={{ marginRight: 6 }} />
              {t("recibos.verNFacturas", { n: detalles.detalles.length })}
            </summary>
            <div style={{ marginTop: 8, paddingLeft: 12 }}>
              {detalles.detalles.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "5px 0",
                    borderBottom: "1px solid var(--border-card)",
                    fontSize: "0.82rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  <span>{d.factura}</span>
                  <span style={{ color: "#4ade80", fontWeight: 600 }}>
                    {t("recibos.abonosAplicados", { monto: formatCurrency(d.abono) })}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}

        {error && (
          <AlertBox type="danger">
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }} />
            {error}
          </AlertBox>
        )}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-muted)",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            {t("common.cancelar")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 20px",
              background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {saving ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("cxp.guardando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" /> {t("recibos.guardarCambios")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
